/** Paid basket association facts; presentation adoption is a separate owner-reviewed stage. */
import * as z from 'zod';
import { insightAnswerMetadataSchema } from './insight-answer.js';
import { insightOrderRecordsSchema } from './insight-supporting-records.js';
export const productPairsDefinition = {
    id: 'product-pairs',
    owner: 'shopify',
    basis: 'current_observed_paid_product_baskets',
    unsupportedClaims: [
        'Association is not causal sales uplift.',
        'Collected orders are not complete lifetime history.',
        'Baskets are not distinct customers, loyalty or future demand.',
        'Orders missing current-parent lines are excluded from observed baskets; present lines do not prove complete ingestion.',
        'Positive retained quantity and price are not accounting net revenue.',
    ],
};
export const productPairsDescriptor = {
    question: 'Which paid products appear together in collected orders?',
    population: 'Current non-test, non-cancelled paid or partially refunded orders containing known products with positive retained quantity and discounted unit price.',
    timeBasis: 'Observed qualifying order creation dates; source synchronization is reported separately.',
    ordering: 'joint_order_count_desc_product_ids_asc',
    visiblePairs: 10,
};
const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const date = z.iso.datetime({ offset: true }).nullable();
const product = z.strictObject({
    productId: z.string().regex(/^[\x21-\x7e]+$/),
    title: z.string().nullable(),
    imageUrl: z.string().nullable(),
    orders: count.refine((value) => value > 0),
});
const pair = z.strictObject({
    a: product,
    b: product,
    together: count.refine((value) => value > 0),
    orders: insightOrderRecordsSchema,
});
export const productPairsCoverageSchema = z.strictObject({
    totalOrders: count,
    eligibleOrders: count,
    excludedOrders: count,
    unknownOrders: count,
    currentLines: count,
    eligibleOrderLines: count,
    missingProduct: count,
    unknownQuantity: count,
    removed: count,
    unknownPrice: count,
    zeroPrice: count,
    eligibleLines: count,
    paidBaskets: count,
    totalPairs: count,
    knownDates: count,
    unknownDates: count,
    firstDate: date,
    lastDate: date,
    ordersSyncedAt: date,
    linesSyncedAt: date,
    eligibleOrdersWithoutLines: count,
});
export const productPairsInputsSchema = z
    .strictObject({
    coverage: productPairsCoverageSchema,
    pairs: z.array(pair),
})
    .superRefine((input, ctx) => {
    const c = input.coverage;
    const countsValid = c.totalOrders === c.eligibleOrders + c.excludedOrders + c.unknownOrders &&
        c.currentLines >= c.eligibleOrderLines &&
        c.eligibleOrderLines ===
            c.missingProduct + c.unknownQuantity + c.removed + c.unknownPrice + c.zeroPrice + c.eligibleLines &&
        c.paidBaskets <= c.eligibleOrders &&
        c.paidBaskets <= c.eligibleLines &&
        (c.paidBaskets === 0) === (c.eligibleLines === 0) &&
        c.eligibleOrdersWithoutLines <= c.eligibleOrders &&
        c.paidBaskets <= c.eligibleOrders - c.eligibleOrdersWithoutLines &&
        c.eligibleOrders - c.eligibleOrdersWithoutLines <= c.eligibleOrderLines &&
        c.knownDates + c.unknownDates === c.paidBaskets &&
        (c.totalOrders === 0) === (c.ordersSyncedAt === null) &&
        (c.currentLines === 0) === (c.linesSyncedAt === null) &&
        (c.knownDates === 0) === (c.firstDate === null && c.lastDate === null) &&
        (c.knownDates === 0 || (c.firstDate !== null && c.lastDate !== null && c.firstDate <= c.lastDate)) &&
        (c.paidBaskets !== 0 || c.totalPairs === 0) &&
        input.pairs.length === c.totalPairs;
    const ids = new Set();
    const products = new Map();
    const orders = new Map();
    let previous;
    const pairsValid = input.pairs.every((p) => {
        const id = JSON.stringify([p.a.productId, p.b.productId]);
        const ordered = !previous ||
            previous.together > p.together ||
            (previous.together === p.together &&
                (previous.a.productId < p.a.productId ||
                    (previous.a.productId === p.a.productId && previous.b.productId < p.b.productId)));
        const consistent = [p.a, p.b].every((product) => {
            const earlier = products.get(product.productId);
            products.set(product.productId, product.orders);
            return earlier === undefined || earlier === product.orders;
        });
        const membershipValid = p.orders.length === p.together &&
            p.orders.every((order) => {
                const previous = orders.get(order.orderId);
                orders.set(order.orderId, order);
                return ((previous === undefined || (previous.name === order.name && previous.createdAt === order.createdAt)) &&
                    (order.createdAt === null ||
                        (c.firstDate !== null &&
                            c.lastDate !== null &&
                            order.createdAt >= c.firstDate &&
                            order.createdAt <= c.lastDate)));
            });
        const valid = p.a.productId < p.b.productId &&
            !ids.has(id) &&
            ordered &&
            consistent &&
            membershipValid &&
            p.a.orders <= c.paidBaskets &&
            p.b.orders <= c.paidBaskets &&
            p.together <= Math.min(p.a.orders, p.b.orders) &&
            p.together >= p.a.orders + p.b.orders - c.paidBaskets;
        ids.add(id);
        previous = p;
        return valid;
    });
    // A maximum spanning forest counts necessary extra product occurrences: in any
    // basket its forest edges are at most (distinct products - 1). Adding N
    // accounts for the first qualifying line in every basket, including unseen products.
    const roots = new Map();
    const root = (id) => {
        let current = id;
        while (roots.has(current))
            current = roots.get(current);
        return current;
    };
    let extraLines = 0;
    for (const p of [...input.pairs].sort((a, b) => b.together - a.together)) {
        const a = root(p.a.productId), b = root(p.b.productId);
        if (a !== b) {
            roots.set(a, b);
            extraLines += p.together;
        }
    }
    const knownOrderDates = [...orders.values()].filter((order) => order.createdAt !== null).length;
    const lineBudgetValid = [...products.values()].reduce((sum, orders) => sum + orders, 0) <= c.eligibleLines &&
        c.paidBaskets + extraLines <= c.eligibleLines &&
        knownOrderDates <= c.knownDates &&
        orders.size - knownOrderDates <= c.unknownDates;
    if (!countsValid || !pairsValid || !lineBudgetValid)
        ctx.addIssue({ code: 'custom', message: 'INSIGHT_PRESENTATION_INVALID: product-pair counts, dates or ordering' });
});
export function productPairsFacts(input) {
    const { coverage, pairs } = productPairsInputsSchema.parse(input);
    let reason = null;
    if (coverage.totalOrders === 0)
        reason = 'no_store_data';
    else if (coverage.paidBaskets === 0 && coverage.eligibleOrdersWithoutLines > 0)
        reason = 'source_incomplete';
    else if (coverage.paidBaskets === 0 &&
        coverage.unknownOrders + coverage.missingProduct + coverage.unknownQuantity + coverage.unknownPrice > 0)
        reason = 'eligibility_unknown';
    return {
        state: reason === null ? 'ready' : 'unavailable',
        reason,
        coverage,
        omittedPairs: Math.max(0, coverage.totalPairs - productPairsDescriptor.visiblePairs),
        pairs: pairs.map((p) => ({
            ...p,
            aShare: p.a.orders / coverage.paidBaskets,
            bShare: p.b.orders / coverage.paidBaskets,
            bGivenA: p.together / p.a.orders,
            aGivenB: p.together / p.b.orders,
            lift: (p.together * coverage.paidBaskets) / (p.a.orders * p.b.orders),
        })),
    };
}
const ratio = z.number().finite().min(0).max(1);
const pairFacts = pair.extend({
    aShare: ratio,
    bShare: ratio,
    bGivenA: ratio,
    aGivenB: ratio,
    lift: z.number().finite().positive(),
});
export const productPairsAnswerSchema = z
    .strictObject({
    ...insightAnswerMetadataSchema.shape,
    definition: z.strictObject({ id: z.literal('product-pairs') }),
    descriptor: z.strictObject({
        question: z.literal(productPairsDescriptor.question),
        population: z.literal(productPairsDescriptor.population),
        timeBasis: z.literal(productPairsDescriptor.timeBasis),
        ordering: z.literal(productPairsDescriptor.ordering),
        visiblePairs: z.literal(10),
    }),
    state: z.enum(['ready', 'unavailable']),
    reason: z.enum(['no_store_data', 'source_incomplete', 'eligibility_unknown']).nullable(),
    coverage: productPairsCoverageSchema,
    omittedPairs: count,
    pairs: z.array(pairFacts),
    actions: z.tuple([]),
})
    .superRefine((answer, ctx) => {
    const input = productPairsInputsSchema.safeParse({
        coverage: answer.coverage,
        pairs: answer.pairs.map(({ a, b, together, orders }) => ({ a, b, together, orders })),
    });
    const timestamps = answer.evidence.sourceTimestamps;
    const valid = input.success &&
        answer.evidence.basis === productPairsDefinition.basis &&
        timestamps.length === 2 &&
        timestamps[0]?.source === 'orders' &&
        timestamps[0]?.syncedAt === answer.coverage.ordersSyncedAt &&
        timestamps[1]?.source === 'order_lines' &&
        timestamps[1]?.syncedAt === answer.coverage.linesSyncedAt &&
        JSON.stringify(productPairsFacts(input.data)) ===
            JSON.stringify({
                state: answer.state,
                reason: answer.reason,
                coverage: answer.coverage,
                omittedPairs: answer.omittedPairs,
                pairs: answer.pairs,
            });
    if (!valid)
        ctx.addIssue({ code: 'custom', message: 'INSIGHT_PRESENTATION_INVALID: product-pair answer differs from facts' });
});
//# sourceMappingURL=product-pairs-answer.js.map